import { ArrowRight, Sparkles, Film, Compass, Landmark, ShieldCheck, HeartHandshake } from 'lucide-react';
import { Link } from 'react-router-dom';

export const AboutUs = ({ onEnquire }) => {
  return (
    <div className="subpage-container" data-testid="about-us-page">
      {/* Subpage Hero */}
      <section className="subpage-hero">
        <div className="subpage-hero-inner">
          <p className="eyebrow"><Sparkles size={14} className="eyebrow-icon" /> WHO WE ARE</p>
          <h1 className="subpage-title">
            Pioneering Wonder.<br />
            <em>Redefining Immersive Journeys.</em>
          </h1>
          <p className="subpage-lead">
            Giraffe Entertainment &amp; Experiences (GEE) creates transformative, multi-sensory attractions that connect people to India's sacred heritage, epic stories, and boundless imagination.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="subpage-body site-container">
        {/* Story Section */}
        <section className="about-story-grid">
          <div className="about-story-text">
            <span className="section-label">OUR STORY</span>
            <h2 className="section-heading">Head &amp; Shoulders Above the Rest.</h2>
            <p>
              Founded under the visionary umbrella of the <strong>Giraffe Group</strong>, GEE emerged from a singular realization: Indian culture and spiritual destinations possess the world's most compelling narratives, yet lack the modern, world-class experiential infrastructure they deserve.
            </p>
            <p>
              We don’t just build screens or exhibits; we craft living portals. Whether it is soaring through the cosmic skies above Ayodhya or witnessing the eternal descent of the holy rivers, our creations combine state-of-the-art cinematic technology with timeless storytelling to leave every guest moved, inspired, and awestruck.
            </p>
            <div className="about-quote-box">
              <p className="about-quote-text">
                "An experience is not something you merely observe from a distance. It is an atmosphere that envelops you, quickens your pulse, and stays with you long after the journey ends."
              </p>
              <span className="about-quote-author">— The GEE Philosophy</span>
            </div>
          </div>

          <div className="about-story-visual">
            <div className="about-image-card">
              <img src="/media/company/immersive.webp" alt="Immersive GEE Cinema Experience" />
              <div className="about-image-caption">
                <h4>Immersive Engineering</h4>
                <p>Designed to engage all five senses with dynamic motion, environmental effects, and 4K stereoscopic visuals.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Four Pillars */}
        <section className="about-pillars-section">
          <div className="section-header-center">
            <span className="section-label">OUR CORE FOUNDATION</span>
            <h2 className="section-heading">The Four Pillars of GEE</h2>
            <p className="section-subtext">Every attraction we develop stands firmly on four guiding principles.</p>
          </div>

          <div className="pillars-grid">
            <div className="pillar-card">
              <div className="pillar-icon"><Film size={28} /></div>
              <h3>Multi-Sensory 7D Technology</h3>
              <p>
                Proprietary motion simulation platforms synced with atmospheric effects — mist, breeze, subtle scents, vibrational depth, and surround acoustics — that place guests right inside the action.
              </p>
            </div>

            <div className="pillar-card">
              <div className="pillar-icon"><Landmark size={28} /></div>
              <h3>Sacred &amp; Cultural Reverence</h3>
              <p>
                Deeply researched, authentic portrayals of India's spiritual corridors and mythological epics, crafted in close collaboration with cultural scholars and local custodians.
              </p>
            </div>

            <div className="pillar-card">
              <div className="pillar-icon"><Compass size={28} /></div>
              <h3>Architectural Integration</h3>
              <p>
                Attractions designed to blend harmoniously into sensitive heritage landscapes, river ghats, and high-footfall pilgrimage zones without disrupting local sanctity.
              </p>
            </div>

            <div className="pillar-card">
              <div className="pillar-icon"><HeartHandshake size={28} /></div>
              <h3>Community &amp; Partnerships</h3>
              <p>
                Collaborating with state tourism boards, temple trusts, and heritage authorities to create sustainable tourism hubs that generate local employment and lasting value.
              </p>
            </div>
          </div>
        </section>

        {/* Flagship Highlight Banner */}
        <section className="about-flagship-highlight">
          <div className="flagship-highlight-content">
            <span className="section-label-gold">LANDMARK ATTRACTION</span>
            <h2>Flagship 7D Cinema at Guptar Ghat, Ayodhya</h2>
            <p>
              Situated along the historic banks of the holy Sarayu river, our Ayodhya flagship welcomes thousands of devotees and global tourists daily, presenting the sacred lore of Sri Ram and Ayodhya with cinematic grandeur.
            </p>
            <div className="flagship-highlight-actions">
              <a
                href="https://guptarghat.com"
                target="_blank"
                rel="noopener noreferrer"
                className="button button-gold"
              >
                Explore Guptar Ghat 7D Cinema <ArrowRight size={17} />
              </a>
              <button
                onClick={() => onEnquire('Flagship 7D Cinema Partnership')}
                className="button button-outline-light"
              >
                Inquire for Partnerships <ArrowRight size={17} />
              </button>
            </div>
          </div>
        </section>

        {/* CTA Strip */}
        <section className="about-bottom-cta">
          <h2>Ready to bring transformative experiences to your venue?</h2>
          <p>We collaborate with governments, heritage trusts, and hospitality destinations across India and internationally.</p>
          <div className="about-cta-buttons">
            <button className="button button-dark" onClick={() => onEnquire('Partnership')}>
              Partner With Us <ArrowRight size={17} />
            </button>
            <Link to="/contact-us" className="button button-gold">
              Contact Our Team <ArrowRight size={17} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};
